import { z } from 'zod';

/**
 * Response of `GET /v1/analytics/embed-url`.
 *
 * Deliberately a discriminated union rather than a nullable URL. QuickSight
 * cannot tell an embedded reader "no row-level-security rule matched you" apart
 * from "you have no hours this period" — both render as an empty dashboard, which
 * says nothing at all to a screen reader. So the API decides, and the SPA renders
 * an explanation instead of an empty frame.
 *
 * Note what the request does NOT accept: any user identifier. The identity comes
 * solely from the verified session. `GenerateEmbedUrlForRegisteredUser` takes the
 * user as a parameter with nothing tying it to the caller, so an endpoint that
 * read an EID from the request would let any approver pull an embed URL for
 * anyone else, RLS faithfully scoping to that person.
 */
export const AnalyticsEmbedResponse = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('ready'),
    /**
     * Single-use bearer token, valid five minutes and redeemable once. Never
     * cache it, never put it in a URL or history entry, never log it.
     */
    embedUrl: z.string().url(),
    /** Minutes the rendered session lasts once the URL is redeemed. */
    sessionLifetimeMinutes: z.number().int().positive(),
  }),
  z.object({
    status: z.literal('no-scope'),
    /** Why there is nothing to show, in words the viewer can act on. */
    reason: z.literal('no-teams'),
  }),
]);
export type AnalyticsEmbedResponse = z.infer<typeof AnalyticsEmbedResponse>;
